<template>
  <Teleport v-if="teleport" to="body">
    <Transition :name="transitionName">
      <div v-if="visible" class="mgx_modal_shell" :class="shellClasses" :style="shellStyle" @click="handleOverlayClick">
        <slot />
      </div>
    </Transition>
  </Teleport>

  <Transition v-else :name="transitionName">
    <div v-if="visible" class="mgx_modal_shell" :class="shellClasses" :style="shellStyle" @click="handleOverlayClick">
      <slot />
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, Teleport, Transition, watch } from 'vue'

let overlayModalCount = 0

const MODAL_ACTIVE_CLASS = 'mgx-modal-open'

const syncRootModalState = () => {
  if (typeof document === 'undefined') {
    return
  }

  document.documentElement.classList.toggle(MODAL_ACTIVE_CLASS, overlayModalCount > 0)
}

const attachOverlayState = () => {
  overlayModalCount += 1
  syncRootModalState()
}

const detachOverlayState = () => {
  overlayModalCount = Math.max(0, overlayModalCount - 1)
  syncRootModalState()
}

const props = withDefaults(
  defineProps<{
    visible: boolean
    teleport?: boolean
    transitionName?: string
    showOverlay?: boolean
    closeOnOverlay?: boolean
    interactive?: boolean
    blurPage?: boolean
    zIndex?: number | string
    padding?: string
    overlayBackground?: string
    overlayBackdropFilter?: string
  }>(),
  {
    teleport: true,
    transitionName: 'mgx-modal-fade',
    showOverlay: true,
    closeOnOverlay: true,
    interactive: true,
    blurPage: true,
    zIndex: 200,
    padding: '24px',
    overlayBackground: 'var(--glass-overlay-bg)',
    overlayBackdropFilter: 'var(--glass-overlay-blur)',
  },
)

const emit = defineEmits<{
  close: []
}>()

const shellClasses = computed(() => ({
  'has-overlay': props.showOverlay,
  'is-interactive': props.interactive,
}))

const shellStyle = computed(() => ({
  zIndex: String(props.zIndex),
  '--mgx-modal-padding': props.padding,
  '--mgx-modal-overlay-bg': props.overlayBackground,
  '--mgx-modal-overlay-filter': props.overlayBackdropFilter,
}))

let overlayStateAttached = false

watch(
  () => [props.visible, props.showOverlay, props.blurPage] as const,
  ([visible, showOverlay, blurPage]) => {
    const shouldAttach = visible && showOverlay && blurPage

    if (shouldAttach && !overlayStateAttached) {
      attachOverlayState()
      overlayStateAttached = true
      return
    }

    if (!shouldAttach && overlayStateAttached) {
      detachOverlayState()
      overlayStateAttached = false
    }
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  if (overlayStateAttached) {
    detachOverlayState()
    overlayStateAttached = false
  }
})

const handleOverlayClick = () => {
  if (props.closeOnOverlay) {
    emit('close')
  }
}
</script>

<style scoped lang="less">
.mgx_modal_shell {
  position: fixed;
  inset: 0;
  pointer-events: none;
  contain: layout paint style;
  isolation: isolate;

  &.is-interactive {
    pointer-events: auto;
  }

  &.has-overlay {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--mgx-modal-padding);
    background: var(--mgx-modal-overlay-bg);
  }
}

:global(html.mgx-modal-open .page_stage_shell) {
  filter: blur(10px) saturate(108%);
  transform: translateZ(0);
  will-change: filter;
  pointer-events: none;
  contain: strict;
}

/*
 * 性能关键优化：当父容器已通过 filter: blur() 模糊时，
 * 子元素的 backdrop-filter 计算完全冗余（模糊之上再模糊），
 * 同时暂停动画和过渡以消除不必要的重绘触发。
 * 每次重绘都会导致 filter: blur() 重新光栅化整棵 DOM 子树，
 * 因此减少重绘次数 = 直接降低 GPU 开销。
 */
:global(html.mgx-modal-open .page_stage_shell *) {
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  animation-play-state: paused !important;
  transition-duration: 0s !important;
  will-change: auto !important;
}

:global(html.mgx-modal-open .app_body),
:global(html.mgx-modal-open .main_container) {
  backface-visibility: hidden;
}

.mgx-modal-fade-enter-active,
.mgx-modal-fade-leave-active {
  transition: opacity var(--transition-base);
}

.mgx-modal-fade-enter-from,
.mgx-modal-fade-leave-to {
  opacity: 0;
}
</style>
